import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ITireTreadManufacturer, defaultValue } from 'app/shared/model/tire-tread-manufacturer.model';

const initialState: EntityState<ITireTreadManufacturer> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/tire-tread-manufacturers';

// Actions

interface IPagedTireTreadManufacturerQueryParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('tireTreadManufacturer/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITireTreadManufacturer[]>(requestUrl);
});

export const getEntitiesByNameSearch = createAsyncThunk(
  'tireTreadManufacturer/fetch_entity_list',
  async ({ page, size, sort, nameSearch }: IPagedTireTreadManufacturerQueryParams) => {
    const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : '?'}cacheBuster=${new Date().getTime()}`;
    return axios.get<ITireTreadManufacturer[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'tireTreadManufacturer/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITireTreadManufacturer>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'tireTreadManufacturer/create_entity',
  async (entity: ITireTreadManufacturer) => {
    const result = await axios.post<ITireTreadManufacturer>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'tireTreadManufacturer/update_entity',
  async (entity: ITireTreadManufacturer) => {
    const result = await axios.put<ITireTreadManufacturer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'tireTreadManufacturer/partial_update_entity',
  async (entity: ITireTreadManufacturer) => {
    const result = await axios.patch<ITireTreadManufacturer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'tireTreadManufacturer/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITireTreadManufacturer>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TireTreadManufacturerSlice = createEntitySlice({
  name: 'tireTreadManufacturer',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesByNameSearch), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesByNameSearch), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TireTreadManufacturerSlice.actions;

// Reducer
export default TireTreadManufacturerSlice.reducer;
